import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useSupplier } from "@/context/SupplierContext";
import { UploadCloud, FileDown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDateTime } from "@/lib/format";

export default function SupplierDocuments() {
  const [docs, setDocs] = useState([]);
  const [pos, setPos] = useState([]);
  const [poId, setPoId] = useState("");
  const [docType, setDocType] = useState("invoice");
  const [uploading, setUploading] = useState(false);
  const { selected, isAll, activeBuyer, buyers, loaded } = useSupplier();

  const orgName = (orgId) => buyers.find((b) => b.org_id === orgId)?.name || "";

  const load = () => {
    api.get(`/documents?buyer_org=${selected}`).then((r) => setDocs(r.data.documents)).catch(() => {});
    api.get(`/pos?buyer_org=${selected}`).then((r) => setPos(r.data.pos)).catch(() => {});
  };
  useEffect(() => { if (loaded) load(); }, [selected, loaded]);

  const upload = async (file) => {
    if (!file || !poId) { toast.error("Select a PO first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      await api.post(`/pos/${poId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setUploading(false); }
  };

  return (
    <div data-testid="supplier-documents-page" className="space-y-4">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Documents</h1>
        <p className="text-sm text-muted-foreground">
          Upload invoices, dispatch, transport and delivery documents · <span className="font-medium text-slate-700">{isAll ? "All Buyer Organizations" : activeBuyer?.name}</span>
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-4">
        <Select value={poId} onValueChange={setPoId}>
          <SelectTrigger data-testid="docs-po-select" className="w-64"><SelectValue placeholder="Select PO *" /></SelectTrigger>
          <SelectContent className="bg-white">
            {pos.map((p) => <SelectItem key={p.id} value={p.id}>{p.po_number}{isAll ? ` — ${p.buyer_org_name}` : ""} — {p.item.slice(0, 30)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={docType} onValueChange={setDocType}>
          <SelectTrigger data-testid="docs-type-select" className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            {[["invoice", "Invoice"], ["dispatch", "Dispatch document"], ["transport", "Transport document"], ["delivery", "Delivery document"], ["other", "Other"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className={`flex items-center gap-1.5 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors ${poId ? "cursor-pointer hover:bg-slate-800" : "cursor-not-allowed opacity-50"}`} data-testid="docs-upload-btn">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Upload Document
          <input type="file" className="hidden" data-testid="docs-upload-input" disabled={!poId || uploading} accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls,.csv,.docx,.txt,.zip" onChange={(e) => upload(e.target.files[0])} />
        </label>
      </div>

      <div className="rounded-lg border bg-card">
        <table className="w-full text-sm" data-testid="documents-table">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-5 py-3 font-medium">Document</th>
              <th className="px-5 py-3 font-medium">Type</th>
              <th className="px-5 py-3 font-medium">PO</th>
              <th className="px-5 py-3 font-medium">Buyer Org</th>
              <th className="px-5 py-3 font-medium">Uploaded By</th>
              <th className="px-5 py-3 font-medium">Date</th>
              <th className="px-5 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {docs.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-muted-foreground">No documents uploaded yet.</td></tr>}
            {docs.map((d) => (
              <tr key={d.id} data-testid={`doc-row-${d.id.slice(0, 8)}`} className="border-b last:border-0 hover:bg-slate-50">
                <td className="px-5 py-3 font-medium">{d.filename}</td>
                <td className="px-5 py-3 capitalize">{d.doc_type}</td>
                <td className="px-5 py-3 font-mono">{d.po_number}</td>
                <td className="px-5 py-3 text-xs">{orgName(d.org_id)}</td>
                <td className="px-5 py-3">{d.uploaded_by}</td>
                <td className="px-5 py-3 font-mono text-xs">{fmtDateTime(d.created_at)}</td>
                <td className="px-5 py-3 text-right">
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/download`} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="ghost" data-testid={`doc-dl-${d.id.slice(0, 8)}`}><FileDown className="h-4 w-4" /></Button>
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
